import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material"
import { FC } from "react"
import { useSearchParams } from "react-router-dom"
import useAccessRole from "../Common/useAccessRole"
import AdminSmartLocks from "../AdminSmartLocks"
import AdminLocks from "../AdminLocks"
import AdminLockOrders from "../AdminLockOrders"
import AdminLockInstallers from "../AdminLockInstallers"
import AdminUnlockRecords from "../AdminUnlockRecords"

// Everything to do with locks, in one place: the TTLock devices, the catalogue of lock models, the orders customers place in the store,
// the installers who fit them, and the unlock records. Who sees which tab follows what the server allows (it checks again on every
// request): the super admin sees all of them, an admin the ones that are not super-admin only.
type Role = 'super' | 'admin'
const TABS: { key: string, label: string, roles: Role[], element: JSX.Element }[] = [
    { key: 'smart-locks', label: 'Smart Locks', roles: ['super', 'admin'], element: <AdminSmartLocks /> },
    { key: 'catalogue', label: 'Catalogue', roles: ['super', 'admin'], element: <AdminLocks /> },
    { key: 'orders', label: 'Orders', roles: ['super'], element: <AdminLockOrders /> },
    { key: 'installers', label: 'Installers', roles: ['super', 'admin'], element: <AdminLockInstallers /> },
    { key: 'unlock-records', label: 'Unlock Records', roles: ['super'], element: <AdminUnlockRecords /> }
]

const AdminLockTabs: FC = () => {
    const role = useAccessRole()
    const [params, setParams] = useSearchParams()

    if (!role)
        return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress size={26} /></Box>
    if (role === 'user')
        return <Typography sx={{ p: 3, color: 'text.secondary' }}>You do not have access to this page.</Typography>

    const tabs = TABS.filter(tab => tab.roles.includes(role))
    const current = tabs.find(tab => tab.key === params.get('tab')) || tabs[0]

    return (
        <Box sx={{ height: '100%' }}>
            <Typography sx={{ fontSize: 25, fontWeight: 'bold', mt: 1 }}>Locks</Typography>
            <Tabs
                value={current.key}
                onChange={(_e, key) => setParams({ tab: key }, { replace: true })}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 1, borderBottom: 1, borderColor: 'divider', maxWidth: 'calc(100% - 40px)' }}
            >
                {tabs.map(tab => <Tab key={tab.key} value={tab.key} label={tab.label} sx={{ textTransform: 'none', fontWeight: 600 }} />)}
            </Tabs>
            {/* only the open tab is mounted, so the others do not load until they are opened */}
            <Box key={current.key}>{current.element}</Box>
        </Box>
    )
}

export default AdminLockTabs
